import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LOCALES = ["en", "zh-CN"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";
export const LANGUAGE_STORAGE_KEY = "media-console-language";

const en = {
  common: {
    brand: "API Media",
    language: {
      label: "Language",
      english: "Switch to English",
      chinese: "Switch to Simplified Chinese"
    },
    navigation: {
      skipToContent: "Skip to content"
    },
    actions: {
      openConsole: "Open console",
      retry: "Try again",
      goHome: "Back to home",
      copy: "Copy",
      copied: "Copied",
      close: "Close"
    },
    errors: {
      notFoundTitle: "Page not found",
      notFoundDescription: "The address is invalid or the page has moved.",
      unexpectedTitle: "Something went wrong",
      unexpectedDescription: "The page could not be loaded. Please try again.",
      unknown: "An unknown error occurred."
    }
  },
  landing: {
    meta: {
      title: "API Media | Image and video generation API",
      description: "Publish and operate image and video generation through one asynchronous API and control plane."
    },
    nav: {
      homeAria: "API Media home",
      primaryAria: "Primary navigation",
      mobileAria: "Mobile navigation",
      workflow: "Workflow",
      capabilities: "Product",
      api: "API",
      operations: "Operations",
      verification: "Verification",
      signIn: "Sign in",
      openConsole: "Open console",
      menuOpen: "Open navigation",
      menuClose: "Close navigation"
    },
    hero: {
      title: "API Media",
      description: "Publish and operate image and video generation through one asynchronous API and control plane.",
      primaryCta: "Open console",
      secondaryCta: "Explore the API",
      support: "Bearer API keys · Idempotent submission"
    },
    demo: {
      title: "Task signal",
      ariaLabel: "Interactive asynchronous task preview",
      modeAria: "Choose a media task type",
      imageTab: "Image",
      videoTab: "Video",
      imageLabel: "Image",
      videoLabel: "Video",
      request: "Request",
      response: "Task state",
      taskStatus: "Task status",
      replay: "Replay task flow",
      copy: "Copy request",
      copySuccess: "Request copied",
      copyError: "Copy failed",
      codeAria: "Example API request",
      fields: {
        model: "Model",
        endpoint: "Endpoint",
        prompt: "Prompt",
        size: "Size",
        mode: "Mode",
        output: "Output",
        resolution: "Resolution",
        quality: "Quality",
        count: "Outputs",
        duration: "Duration",
        ratio: "Ratio",
        status: "Status"
      },
      image: {
        model: "gpt-image-2",
        endpoint: "POST /v1/images/jobs",
        prompt: "Editorial product photograph, soft daylight, precise texture",
        mode: "text_to_image",
        output: "2048x2048",
        resolution: "2K",
        quality: "medium",
        count: "2",
        queued: "Request accepted and queued",
        running: "Image generation in progress",
        storing: "Generated image is being stored",
        ready: "Stored image is ready"
      },
      video: {
        model: "seedance2-pro",
        endpoint: "POST /v1/videos",
        prompt: "Slow camera push through a quiet studio, natural motion",
        mode: "text_to_video",
        output: "480p",
        resolution: "480p",
        duration: "4 sec",
        ratio: "9:16",
        queued: "Request accepted and queued",
        running: "Video generation in progress",
        storing: "Generated video is being stored",
        ready: "Stored video is ready"
      },
      status: {
        ariaLabel: "Task progress",
        queued: "Queued",
        running: "Running",
        storing: "Storing",
        ready: "Ready"
      }
    },
    proof: {
      ariaLabel: "API Media specifications",
      modelsValue: "5",
      models: "launch models",
      imageValue: "Images",
      imageOutput: "· 1K–4K",
      videoValue: "Video",
      videoDuration: "· 4–15 sec",
      asyncValue: "Asynchronous",
      asyncLabel: "API"
    },
    workflow: {
      title: "One request. A controlled path to the result.",
      description: "Submit once, follow every state and retrieve only validated stored media.",
      submit: {
        title: "Submit",
        description: "Submit an authenticated image or video request as an asynchronous task."
      },
      snapshot: {
        title: "Run",
        description: "Workers prepare inputs, dispatch upstream work and track generation progress."
      },
      process: {
        title: "Store",
        description: "Validate completed media and transfer it into controlled object storage."
      },
      retrieve: {
        title: "Retrieve",
        description: "Poll by task ID and receive signed URLs only after storage succeeds."
      }
    },
    capabilities: {
      title: "Image and video generation, one control plane",
      description: "A focused launch catalog covers image creation, image editing and four video generation modes.",
      modelLabel: "Model",
      modelsLabel: "Models",
      modesLabel: "Modes",
      outputLabel: "Output",
      labels: {
        model: "Model",
        modes: "Modes",
        resolution: "Resolution",
        quality: "Quality",
        outputs: "Outputs",
        duration: "Duration",
        ratio: "Ratio"
      },
      image: {
        title: "Image generation",
        model: "gpt-image-2 / nanobananapro / nanobanana2",
        description: "Create or transform images with model-specific quality, resolution and output-count controls.",
        modes: "Text to image / Image to image",
        output: "1K / 2K / 4K · low / medium / high",
        resolution: "1K / 2K / 4K",
        quality: "Low / Medium / High",
        outputs: "1–4, depending on model"
      },
      video: {
        title: "Video generation",
        models: "seedance2-fast / seedance2-mini / seedance2-pro",
        description: "Generate video from text, keyframes or reference media with a normalized asynchronous task contract.",
        modes: "Text / Image / Start-end / Reference",
        duration: "4-15 seconds",
        ratio: "9:16 / 16:9"
      }
    },
    marketplace: {
      title: "Built for the AggAPI marketplace",
      description: "Publish model capabilities, validate seller configuration and accept asynchronous media jobs through explicit endpoints.",
      auth: "Bearer authentication",
      idempotency: "Idempotency-Key supported",
      pointManifest: "Manifest and configuration validation for published model variants.",
      pointAsync: "Asynchronous image and video creation with task polling.",
      pointAuth: "Bearer authentication with conflict-safe idempotency keys.",
      codeTitle: "Create an image task",
      copy: "Copy API example",
      copySuccess: "API example copied",
      copyError: "Could not copy the API example",
      codeAria: "Marketplace API request example",
      endpoints: {
        manifest: {
          method: "GET",
          path: "/aggapi/v1/manifest",
          description: "Expose enabled, published models and variants."
        },
        validate: {
          method: "POST",
          path: "/aggapi/v1/validate",
          description: "Check seller configuration without paid generation."
        },
        image: {
          method: "POST",
          path: "/v1/images/jobs",
          description: "Create an asynchronous image task."
        },
        video: {
          method: "POST",
          path: "/v1/videos",
          description: "Create an asynchronous video task."
        }
      }
    },
    operations: {
      title: "Operate generation, not just requests.",
      description: "The console keeps task delivery, pricing and provider governance visible without changing the public task contract.",
      idempotency: {
        title: "Conflict-safe submission",
        description: "Replay the same request safely; reject reuse of a key with a different payload."
      },
      pricing: {
        title: "Immutable price snapshots",
        description: "Each job retains the published price version, variant and unit price used at submission."
      },
      storage: {
        title: "Controlled result storage",
        description: "Validate and transfer generated assets before returning stored result URLs."
      },
      visibility: {
        title: "Operational visibility",
        description: "Inspect task state, provider attempts and audit history from the console."
      },
      items: {
        idempotency: {
          title: "Conflict-safe submission",
          description: "Replay the same request safely; reject reuse of a key with a different payload."
        },
        pricing: {
          title: "Immutable price snapshots",
          description: "Each job retains the published price version, variant and unit price used at submission."
        },
        storage: {
          title: "Controlled result storage",
          description: "Validate and transfer generated assets before returning stored result URLs."
        },
        governance: {
          title: "Release governance",
          description: "Manage providers, model publication, complete price matrices and audit history from one console."
        }
      }
    },
    verification: {
      title: "Verified end to end",
      description: "The paid E2E harness is opt-in, budget-capped and designed to verify the full buyer-to-seller path against real providers.",
      realUpstream: "Runs image and video generation against configured real upstream providers.",
      billing: "Asserts buyer balance, seller ledger entries and marketplace fees.",
      transfer: "Confirms generated results are transferred into controlled storage.",
      replay: "Verifies idempotent replay without a duplicate upstream video submission.",
      commandLabel: "Verification command",
      command: "make paid-e2e",
      checks: {
        budget: "Refuses to submit unless the declared cost stays within the explicit budget.",
        replay: "Checks buyer submission, idempotent replay and task polling through AggAPI.",
        accounting: "Asserts token balance, seller ledger entries and marketplace fees.",
        delivery: "Confirms result transfer and a single upstream video submission."
      },
      note: "Verification output excludes credentials, source media URLs and signed result URLs."
    },
    finalCta: {
      title: "Move generation into one control plane.",
      description: "Open the console to create tasks, issue API keys and operate the published model catalog.",
      action: "Open console",
      button: "Open console"
    },
    footer: {
      description: "Asynchronous image and video infrastructure for API-first delivery.",
      workflow: "Workflow",
      api: "API",
      console: "Console",
      navAria: "Footer navigation",
      note: "Built for traceable asynchronous delivery.",
      copyright: "API Media"
    }
  },
  login: {
    meta: {
      title: "Sign in | API Media",
      description: "Sign in to operate API Media image and video generation tasks."
    },
    brand: "API Media",
    title: "Sign in to the console",
    description: "Manage generation tasks, API keys and seller configuration.",
    fields: {
      username: "Username",
      usernamePlaceholder: "demo_user",
      password: "Password"
    },
    actions: {
      submit: "Sign in",
      submitting: "Signing in",
      showPassword: "Show password",
      hidePassword: "Hide password"
    },
    registrationPrompt: "New to API Media?",
    registrationAction: "Create an account",
    accountSecurity: "Your password is protected with secure hashing",
    signal: {
      ariaLabel: "Supported launch models",
      title: "One system for image and video tasks",
      image: "IMAGE",
      video: "VIDEO"
    },
    errors: {
      invalidCredentials: "The username or password is incorrect.",
      invalidRequest: "Enter a valid username and password.",
      network: "The console could not be reached. Check your connection and try again.",
      unknown: "Sign-in failed. Please try again."
    }
  },
  register: {
    meta: {
      title: "Create account | API Media",
      description: "Create an API Media account to submit image and video generation tasks."
    },
    brand: "API Media",
    title: "Create your account",
    description: "Register with a username and password to access the console.",
    fields: {
      username: "Username",
      usernamePlaceholder: "demo_user",
      usernameHint: "Any non-empty username; letter case is ignored.",
      password: "Password",
      passwordHint: "At least 7 characters."
    },
    actions: {
      submit: "Create account",
      submitting: "Creating account",
      showPassword: "Show password",
      hidePassword: "Hide password"
    },
    loginPrompt: "Already have an account?",
    loginAction: "Sign in",
    zeroBalance: "New accounts start with 0 points",
    errors: {
      invalidUsername: "Enter a username.",
      invalidPassword: "Use at least 7 characters.",
      usernameTaken: "That username is already registered.",
      rateLimited: "Too many registration attempts. Try again later.",
      invalidRequest: "Check the username and password requirements.",
      network: "The console could not be reached. Check your connection and try again.",
      unknown: "Account creation failed. Please try again."
    }
  }
} as const;

type TranslationShape<T> = {
  readonly [Key in keyof T]: T[Key] extends string ? string : TranslationShape<T[Key]>;
};

const zhCN = {
  common: {
    brand: "API Media",
    language: {
      label: "语言",
      english: "切换到英文",
      chinese: "切换到简体中文"
    },
    navigation: {
      skipToContent: "跳到主要内容"
    },
    actions: {
      openConsole: "打开控制台",
      retry: "重试",
      goHome: "返回首页",
      copy: "复制",
      copied: "已复制",
      close: "关闭"
    },
    errors: {
      notFoundTitle: "页面不存在",
      notFoundDescription: "请求的地址无效或页面已经移动。",
      unexpectedTitle: "出现错误",
      unexpectedDescription: "页面无法加载，请重试。",
      unknown: "发生未知错误。"
    }
  },
  landing: {
    meta: {
      title: "API Media | 图片与视频生成 API",
      description: "通过一个异步 API 与控制台，发布并运营图片和视频生成服务。"
    },
    nav: {
      homeAria: "API Media 首页",
      primaryAria: "主导航",
      mobileAria: "移动端导航",
      workflow: "工作流",
      capabilities: "产品",
      api: "API",
      operations: "运维",
      verification: "验证",
      signIn: "登录",
      openConsole: "打开控制台",
      menuOpen: "打开导航",
      menuClose: "关闭导航"
    },
    hero: {
      title: "API Media",
      description: "通过一个异步 API 与控制台，发布并运营图片和视频生成服务。",
      primaryCta: "打开控制台",
      secondaryCta: "查看 API",
      support: "Bearer API 密钥 · 幂等提交"
    },
    demo: {
      title: "任务信号",
      ariaLabel: "异步任务交互预览",
      modeAria: "选择媒体任务类型",
      imageTab: "图片",
      videoTab: "视频",
      imageLabel: "图片",
      videoLabel: "视频",
      request: "请求",
      response: "任务状态",
      taskStatus: "任务状态",
      replay: "重新播放任务流程",
      copy: "复制请求",
      copySuccess: "请求已复制",
      copyError: "复制失败",
      codeAria: "API 请求示例",
      fields: {
        model: "模型",
        endpoint: "端点",
        prompt: "提示词",
        size: "尺寸",
        mode: "模式",
        output: "输出",
        resolution: "分辨率",
        quality: "质量",
        count: "输出",
        duration: "时长",
        ratio: "画幅",
        status: "状态"
      },
      image: {
        model: "gpt-image-2",
        endpoint: "POST /v1/images/jobs",
        prompt: "柔和日光下的编辑风产品摄影，纹理细节准确",
        mode: "text_to_image",
        output: "2048x2048",
        resolution: "2K",
        quality: "medium",
        count: "2",
        queued: "请求已接收并进入队列",
        running: "正在生成图片",
        storing: "正在转存生成的图片",
        ready: "已转存的图片可以使用"
      },
      video: {
        model: "seedance2-pro",
        endpoint: "POST /v1/videos",
        prompt: "镜头缓慢推进安静的影棚，运动自然",
        mode: "text_to_video",
        output: "480p",
        resolution: "480p",
        duration: "4 秒",
        ratio: "9:16",
        queued: "请求已接收并进入队列",
        running: "正在生成视频",
        storing: "正在转存生成的视频",
        ready: "已转存的视频可以使用"
      },
      status: {
        ariaLabel: "任务进度",
        queued: "已排队",
        running: "处理中",
        storing: "转存中",
        ready: "已就绪"
      }
    },
    proof: {
      ariaLabel: "API Media 规格",
      modelsValue: "5",
      models: "个首发模型",
      imageValue: "图片",
      imageOutput: "· 1K–4K",
      videoValue: "视频",
      videoDuration: "· 4–15 秒",
      asyncValue: "异步",
      asyncLabel: "API"
    },
    workflow: {
      title: "一次请求，一条可控的结果链路。",
      description: "提交一次，追踪每个状态，只在结果验证转存后获取媒体。",
      submit: {
        title: "提交",
        description: "使用认证信息提交图片或视频异步任务。"
      },
      snapshot: {
        title: "执行",
        description: "工作进程准备输入、调度上游任务并追踪生成进度。"
      },
      process: {
        title: "转存",
        description: "验证完成的媒体，并将其转存到受控对象存储。"
      },
      retrieve: {
        title: "获取",
        description: "通过任务 ID 查询，仅在转存成功后获取签名 URL。"
      }
    },
    capabilities: {
      title: "图片与视频生成，一套控制平面",
      description: "聚焦的首发模型目录覆盖图片生成、图片编辑与四种视频生成模式。",
      modelLabel: "模型",
      modelsLabel: "模型",
      modesLabel: "模式",
      outputLabel: "输出",
      labels: {
        model: "模型",
        modes: "模式",
        resolution: "分辨率",
        quality: "质量",
        outputs: "输出",
        duration: "时长",
        ratio: "画幅"
      },
      image: {
        title: "图片生成",
        model: "gpt-image-2 / nanobananapro / nanobanana2",
        description: "通过每个模型支持的质量、分辨率和输出数量控制生成或转换图片。",
        modes: "文生图 / 图生图",
        output: "1K / 2K / 4K · 低 / 中 / 高",
        resolution: "1K / 2K / 4K",
        quality: "低 / 中 / 高",
        outputs: "1–4，取决于模型"
      },
      video: {
        title: "视频生成",
        models: "seedance2-fast / seedance2-mini / seedance2-pro",
        description: "通过统一的异步任务契约，从文本、关键帧或参考媒体生成视频。",
        modes: "文本 / 图片 / 首尾帧 / 参考素材",
        duration: "4-15 秒",
        ratio: "9:16 / 16:9"
      }
    },
    marketplace: {
      title: "为 AggAPI Marketplace 构建",
      description: "通过明确的端点发布模型能力、验证卖家配置，并接收异步媒体任务。",
      auth: "Bearer 身份验证",
      idempotency: "支持 Idempotency-Key",
      pointManifest: "为已发布的模型变体提供清单与配置验证。",
      pointAsync: "异步创建图片和视频，并通过任务查询追踪进度。",
      pointAuth: "Bearer 身份验证与带冲突保护的幂等键。",
      codeTitle: "创建图片任务",
      copy: "复制 API 示例",
      copySuccess: "API 示例已复制",
      copyError: "无法复制 API 示例",
      codeAria: "Marketplace API 请求示例",
      endpoints: {
        manifest: {
          method: "GET",
          path: "/aggapi/v1/manifest",
          description: "发布已启用的模型和变体。"
        },
        validate: {
          method: "POST",
          path: "/aggapi/v1/validate",
          description: "无需付费生成即可检查卖家配置。"
        },
        image: {
          method: "POST",
          path: "/v1/images/jobs",
          description: "创建异步图片任务。"
        },
        video: {
          method: "POST",
          path: "/v1/videos",
          description: "创建异步视频任务。"
        }
      }
    },
    operations: {
      title: "运营生成服务，而不只是发送请求。",
      description: "控制台集中呈现任务交付、定价和上游治理，同时保持公共任务契约稳定。",
      idempotency: {
        title: "冲突保护",
        description: "相同请求可安全重放；使用同一幂等键提交不同载荷时会被拒绝。"
      },
      pricing: {
        title: "不可变价格快照",
        description: "每个任务保留提交时使用的已发布价格版本、变体与单价。"
      },
      storage: {
        title: "受控结果转存",
        description: "返回转存结果 URL 前，对生成资产进行验证与转存。"
      },
      visibility: {
        title: "运营可见性",
        description: "在控制台中检查任务状态、上游尝试与审计历史。"
      },
      items: {
        idempotency: {
          title: "冲突保护",
          description: "相同请求可安全重放；使用同一幂等键提交不同载荷时会被拒绝。"
        },
        pricing: {
          title: "不可变价格快照",
          description: "每个任务保留提交时使用的已发布价格版本、变体与单价。"
        },
        storage: {
          title: "受控结果转存",
          description: "返回转存结果 URL 前，对生成资产进行验证与转存。"
        },
        governance: {
          title: "发布治理",
          description: "在一个控制台中管理上游、模型发布、完整价格矩阵与审计记录。"
        }
      }
    },
    verification: {
      title: "真实端到端验证",
      description: "真实付费 E2E 验证由操作者显式开启并受预算上限约束，用于核验从买家到卖家的完整链路。",
      realUpstream: "使用已配置的真实上游运行图片和视频生成。",
      billing: "断言买家余额、卖家流水与 Marketplace 手续费。",
      transfer: "确认生成结果已转存到受控存储。",
      replay: "验证幂等重放不会造成重复的上游视频提交。",
      commandLabel: "验证命令",
      command: "make paid-e2e",
      checks: {
        budget: "仅当声明成本不超过显式预算时才允许提交。",
        replay: "通过 AggAPI 检查买家提交、幂等重放与任务查询。",
        accounting: "断言 Token 余额、卖家流水与 Marketplace 手续费。",
        delivery: "确认结果转存，并保证视频只向上游提交一次。"
      },
      note: "验证输出不会包含凭据、源素材 URL 或签名结果 URL。"
    },
    finalCta: {
      title: "将生成服务纳入一个控制台。",
      description: "打开控制台，创建任务、签发 API 密钥并运营已发布的模型目录。",
      action: "打开控制台",
      button: "打开控制台"
    },
    footer: {
      description: "面向 API 优先交付的异步图片与视频基础设施。",
      workflow: "工作流",
      api: "API",
      console: "控制台",
      navAria: "页脚导航",
      note: "为可追踪的异步交付而构建。",
      copyright: "API Media"
    }
  },
  login: {
    meta: {
      title: "登录 | API Media",
      description: "登录并运营 API Media 图片与视频生成任务。"
    },
    brand: "API Media",
    title: "登录控制台",
    description: "管理生成任务、API 密钥和卖家配置。",
    fields: {
      username: "用户名",
      usernamePlaceholder: "demo_user",
      password: "密码"
    },
    actions: {
      submit: "登录",
      submitting: "正在登录",
      showPassword: "显示密码",
      hidePassword: "隐藏密码"
    },
    registrationPrompt: "还没有 API Media 账户？",
    registrationAction: "注册账户",
    accountSecurity: "密码将使用安全哈希保护",
    signal: {
      ariaLabel: "支持的首发模型",
      title: "统一处理图片与视频任务",
      image: "图片",
      video: "视频"
    },
    errors: {
      invalidCredentials: "用户名或密码不正确。",
      invalidRequest: "请输入有效的用户名和密码。",
      network: "无法连接控制台，请检查网络后重试。",
      unknown: "登录失败，请重试。"
    }
  },
  register: {
    meta: {
      title: "注册 | API Media",
      description: "注册 API Media 账户并提交图片与视频生成任务。"
    },
    brand: "API Media",
    title: "创建账户",
    description: "使用用户名和密码注册后即可进入控制台。",
    fields: {
      username: "用户名",
      usernamePlaceholder: "demo_user",
      usernameHint: "用户名不能为空，不区分大小写。",
      password: "密码",
      passwordHint: "至少 7 个字符。"
    },
    actions: {
      submit: "注册账户",
      submitting: "正在注册",
      showPassword: "显示密码",
      hidePassword: "隐藏密码"
    },
    loginPrompt: "已有账户？",
    loginAction: "登录",
    zeroBalance: "新账户初始积分为 0",
    errors: {
      invalidUsername: "请输入用户名。",
      invalidPassword: "密码至少 7 个字符。",
      usernameTaken: "该用户名已被注册。",
      rateLimited: "注册尝试过于频繁，请稍后再试。",
      invalidRequest: "请检查用户名和密码是否符合要求。",
      network: "无法连接控制台，请检查网络后重试。",
      unknown: "注册失败，请重试。"
    }
  }
} as const satisfies TranslationShape<typeof en>;

export const resources = {
  en: { translation: en },
  "zh-CN": { translation: zhCN }
} as const;

export const DEFAULT_NAMESPACE = "translation";

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: typeof DEFAULT_NAMESPACE;
    resources: (typeof resources)["en"];
    returnNull: false;
  }
}

export function resolveLocale(value: unknown): Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ? (value as Locale)
    : DEFAULT_LOCALE;
}

function browserStorage(): Pick<Storage, "getItem" | "setItem"> | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function resolveStoredLocale(storage = browserStorage()): Locale {
  try {
    return resolveLocale(storage?.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function syncDocumentLocale(locale: Locale): void {
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}

function persistLocale(locale: Locale): void {
  try {
    browserStorage()?.setItem(LANGUAGE_STORAGE_KEY, locale);
  } catch {
    // Language switching still works when storage is unavailable.
  }
}

const initialLocale = resolveStoredLocale();
syncDocumentLocale(initialLocale);

export const i18n = createInstance();

i18n.on("languageChanged", (language) => {
  const locale = resolveLocale(language);
  persistLocale(locale);
  syncDocumentLocale(locale);
});

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  load: "currentOnly",
  defaultNS: DEFAULT_NAMESPACE,
  ns: [DEFAULT_NAMESPACE],
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  returnNull: false,
  initAsync: false
});

export function changeLocale(locale: Locale): Promise<unknown> {
  persistLocale(locale);
  syncDocumentLocale(locale);
  return i18n.changeLanguage(locale);
}

export default i18n;
